import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, CloudDownload, Printer } from 'lucide-react'
import Toast from '../../../components/Toast/Toast'
import { useAuth } from '../../../contexts/AuthContext'
import { dutyRosterService } from '../../../services/dutyRosterApi'
import { useToast } from '../../../hooks/useToast'
import { downloadDutySheet, printDutySheet, rosterFileName } from './dutyRosterPrint'
import '../Financeiro/Dashboard/FinanceDashboardPage.css'
import './DutyRosterPage.css'

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']

function currentMonth() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(new Date())
  return {
    year: Number(parts.find((part) => part.type === 'year').value),
    month: Number(parts.find((part) => part.type === 'month').value)
  }
}

function weekday(iso) {
  const [year, month, day] = String(iso).split('-').map(Number)
  return WEEKDAYS[new Date(year, month - 1, day).getDay()] || ''
}

function messageFrom(error, fallback) {
  return error?.response?.data?.message || fallback
}

function monthFromQuery(yearValue, monthValue) {
  const year = Number(yearValue)
  const month = Number(monthValue)
  if (year >= 2000 && year <= 2100 && month >= 1 && month <= 12) return { year, month }
  return null
}

export default function MyDutyPage() {
  const [searchParams] = useSearchParams()
  const requested = monthFromQuery(searchParams.get('ano'), searchParams.get('mes'))
  const initial = requested || currentMonth()
  const { user, organizations, currentSchema } = useAuth()
  const { toast, showToast, hideToast } = useToast()
  const [year, setYear] = useState(initial.year)
  const [month, setMonth] = useState(initial.month)
  const [shifts, setShifts] = useState([])
  const queryYear = searchParams.get('ano')
  const queryMonth = searchParams.get('mes')

  useEffect(() => {
    const next = monthFromQuery(queryYear, queryMonth)
    if (!next) return
    setYear(next.year)
    setMonth(next.month)
  }, [queryYear, queryMonth])

  const load = useCallback(async () => {
    const response = await dutyRosterService.mine({ year, month })
    setShifts(response.data?.data || [])
  }, [year, month])

  useEffect(() => {
    load().catch((err) => showToast(messageFrom(err, 'Não foi possível carregar a sua escala'), 'error'))
  }, [load, showToast])

  const changeMonth = (delta) => {
    const cursor = new Date(year, month - 1 + delta, 1)
    setYear(cursor.getFullYear())
    setMonth(cursor.getMonth() + 1)
  }

  const sheetModel = () => {
    const schema = currentSchema || user?.organization_schema
    const organization = (organizations || []).find((item) => item.schema_name === schema)
    return {
      title: `Meus plantões · ${user?.name || 'Plantonista'}`,
      subtitle: `${MONTHS[month - 1]} ${year}`,
      clinicName: organization?.label || user?.organization_label || '',
      issuedBy: user?.name || '',
      issuedAt: new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
      columns: ['Dia', 'Semana', 'Paciente', 'Turno', 'Horário'],
      rows: shifts.map((shift) => [
        String(shift.shift_date).split('-').reverse().join('/'),
        weekday(shift.shift_date),
        shift.bed ? `${shift.patient_name} · ${shift.bed}` : shift.patient_name,
        shift.slot === 'night' ? 'Noturno' : 'Diurno',
        `${shift.start_time}–${shift.end_time}`
      ])
    }
  }

  const printSheet = () => {
    const opened = printDutySheet(sheetModel())
    if (!opened) showToast('Permita janelas pop-up para imprimir a escala.', 'warning')
  }

  const downloadSheet = async () => {
    try {
      await downloadDutySheet(sheetModel(), rosterFileName(user?.name || 'minha-escala', MONTHS[month - 1], year))
    } catch {
      showToast('Não foi possível baixar a escala.', 'error')
    }
  }

  return (
    <div className="finance-dashboard patients-module duty-roster-page">
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} />
      <div className="duty-roster-toolbar">
        <div>
          <strong>Meus plantões</strong>
          <p className="duty-roster-hint">Os dias e horários marcados para você.</p>
        </div>
        <div className="duty-roster-month-nav">
          <button type="button" className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive" onClick={downloadSheet}>
            <CloudDownload size={14} /> Baixar
          </button>
          <button type="button" className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive" onClick={printSheet}>
            <Printer size={14} /> Imprimir
          </button>
          <button type="button" className="duty-roster-icon-btn" onClick={() => changeMonth(-1)} aria-label="Mês anterior"><ChevronLeft size={16} /></button>
          <strong>{MONTHS[month - 1]} {year}</strong>
          <button type="button" className="duty-roster-icon-btn" onClick={() => changeMonth(1)} aria-label="Próximo mês"><ChevronRight size={16} /></button>
        </div>
      </div>
      <section className="duty-roster-panel">
        <table className="duty-roster-table">
          <thead>
            <tr>
              <th>Dia</th>
              <th>Paciente</th>
              <th>Turno</th>
              <th>Horário</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((shift) => (
              <tr key={shift.id}>
                <td>{String(shift.shift_date).split('-').reverse().join('/')} · {weekday(shift.shift_date)}</td>
                <td>{shift.patient_name}{shift.bed ? ` · ${shift.bed}` : ''}</td>
                <td>{shift.slot === 'night' ? 'Noturno' : 'Diurno'}</td>
                <td>{shift.start_time}–{shift.end_time}</td>
              </tr>
            ))}
            {!shifts.length && (
              <tr><td colSpan={4}>Nenhum plantão neste mês.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  )
}
